<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <no-projects-alert v-if="noProjects"></no-projects-alert>
    <!-- Header with project chips and controls -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto" class="d-flex align-center ga-2 flex-wrap">
        <!-- Project chips -->
        <v-chip
          v-for="project in projects"
          :key="project.id"
          :color="selectedProjectId === project.id ? 'primary' : 'default'"
          :variant="selectedProjectId === project.id ? 'elevated' : 'outlined'"
          @click="selectProject(project.id)"
          class="cursor-pointer"
        >
          {{ project.name }}
        </v-chip>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <div class="d-flex align-center ga-2">
          <span class="text-caption font-weight-bold">Найдено: {{ totalEntities }}</span>
          <v-btn
            :icon="panelVisible ? 'mdi-filter-off' : 'mdi-filter'"
            variant="outlined"
            @click="panelVisible = !panelVisible"
            :color="hasActiveFilters ? 'primary' : 'default'"
          />
        </div>
      </v-col>
    </v-row>

    <v-row>
      <!-- Main content column -->
      <v-col :cols="panelVisible ? 10 : 12" class="table-column-transition">
        <v-card>
          <!-- Entity type tabs -->
          <div class="d-flex align-center">
          <v-tabs v-model="activeTab" color="primary" density="compact">
            <v-tab value="PER">
              <v-icon start size="small">mdi-account</v-icon>
              Персоны ({{ typeCounts.PER }})
            </v-tab>
            <v-tab value="ORG">
              <v-icon start size="small">mdi-domain</v-icon>
              Организации ({{ typeCounts.ORG }})
            </v-tab>
            <v-tab value="LOC">
              <v-icon start size="small">mdi-map-marker</v-icon>
              Локации ({{ typeCounts.LOC }})
            </v-tab>
            <v-tab value="GPE">
              <v-icon start size="small">mdi-earth</v-icon>
              Страны ({{ typeCounts.GPE }})
            </v-tab>
          </v-tabs>
          <HelpHint section="entities" text="Что такое сущности, карточка и объединение дублей" class="ml-1" />
          </div>

          <!-- Bulk actions bar -->
          <div class="bulk-actions-bar pa-2 d-flex align-center ga-2">
            <v-select
              v-model="selectedBulkAction"
              :items="bulkActionsForSelect"
              item-title="title"
              item-value="value"
              placeholder="Выберите действие"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              :disabled="selectedEntities.length === 0"
              :loading="bulkActionLoading"
              @update:model-value="executeBulkAction"
              style="min-width: 200px; max-width: 200px"
            ></v-select>
            <span v-if="selectedEntities.length > 0" class="text-body-2 text-medium-emphasis">
              Выбрано: {{ selectedEntities.length }}
            </span>
          </div>

          <v-card-text class="pa-0">
            <!-- Entity table -->
            <div
              ref="entitiesContainer"
              class="entities-container"
              @scroll="handleScroll"
            >
              <v-table density="compact" hover>
                <thead>
                  <tr>
                    <th style="width: 40px">
                      <v-checkbox
                        :model-value="allSelected"
                        :indeterminate="someSelected"
                        hide-details
                        density="compact"
                        @update:model-value="toggleSelectAll"
                      />
                    </th>
                    <th style="width: 40px"></th>
                    <th class="sortable-header" @click="toggleSort('display_name')">
                      Название
                      <v-icon v-if="sortBy === 'display_name'" size="small" class="ml-1">
                        {{ sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                      </v-icon>
                    </th>
                    <th class="sortable-header text-center" style="width: 120px" @click="toggleSort('mention_count')">
                      Упоминания
                      <v-icon v-if="sortBy === 'mention_count'" size="small" class="ml-1">
                        {{ sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                      </v-icon>
                    </th>
                    <th class="sortable-header text-center" style="width: 150px" @click="toggleSort('first_seen_at')">
                      Первое
                      <v-icon v-if="sortBy === 'first_seen_at'" size="small" class="ml-1">
                        {{ sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                      </v-icon>
                    </th>
                    <th class="sortable-header text-center" style="width: 150px" @click="toggleSort('last_seen_at')">
                      Последнее
                      <v-icon v-if="sortBy === 'last_seen_at'" size="small" class="ml-1">
                        {{ sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                      </v-icon>
                    </th>
                    <th style="width: 80px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="isLoading && entities.length === 0">
                    <td colspan="7" class="text-center pa-8">
                      <v-progress-circular indeterminate color="primary" />
                      <div class="mt-2">Загрузка сущностей...</div>
                    </td>
                  </tr>
                  <tr v-else-if="entities.length === 0">
                    <td colspan="7" class="text-center pa-8">
                      <v-icon size="48" color="grey-lighten-1">mdi-account-off</v-icon>
                      <div class="mt-2 text-grey">Нет сущностей для отображения</div>
                    </td>
                  </tr>
                  <tr
                    v-else
                    v-for="entity in entities"
                    :key="entity.id"
                    @click="openEntityModal(entity)"
                    class="entity-row cursor-pointer"
                    :class="{ 'entity-selected': selectedEntities.includes(entity.id) }"
                  >
                    <td class="text-center" @click.stop>
                      <v-checkbox
                        :model-value="selectedEntities.includes(entity.id)"
                        hide-details
                        density="compact"
                        @update:model-value="toggleEntitySelection(entity.id)"
                      />
                    </td>
                    <td class="text-center">
                      <v-icon
                        :color="entity.is_favorite ? 'amber' : 'grey-lighten-1'"
                        :icon="entity.is_favorite ? 'mdi-star' : 'mdi-star-outline'"
                        size="small"
                        class="favorite-star"
                        :class="{ 'favorite-active': entity.is_favorite }"
                        @click.stop="toggleFavorite(entity)"
                        title="Добавить в избранное"
                      />
                    </td>
                    <td>
                      <div class="d-flex align-center">
                        <v-icon :color="getEntityIconColor(entity.entity_type)" size="small" class="mr-2">
                          {{ getEntityIcon(entity.entity_type) }}
                        </v-icon>
                        <span class="font-weight-medium">{{ entity.display_name }}</span>
                      </div>
                    </td>
                    <td class="text-center">
                      <v-chip
                        size="small"
                        variant="tonal"
                        color="primary"
                        class="cursor-pointer"
                        @click.stop="openMessagesForEntity(entity)"
                        title="Показать сообщения с этой сущностью"
                      >
                        {{ entity.mention_count }}
                      </v-chip>
                    </td>
                    <td class="text-center text-caption">
                      {{ formatDateShort(entity.first_seen_at) }}
                    </td>
                    <td class="text-center text-caption">
                      {{ formatDateShort(entity.last_seen_at) }}
                    </td>
                    <td class="text-center">
                      <v-icon v-if="entity.is_excluded" color="error" size="small" title="Исключена">
                        mdi-eye-off
                      </v-icon>
                      <v-icon v-if="entity.is_tracked" color="success" size="small" title="Отслеживается">
                        mdi-eye-check
                      </v-icon>
                      <v-icon v-if="entity.user_notes" color="info" size="small" class="ml-1" title="Есть примечания">
                        mdi-note-text
                      </v-icon>
                    </td>
                  </tr>
                </tbody>
              </v-table>

              <!-- Loading more indicator -->
              <div v-if="isLoading && entities.length > 0" class="text-center pa-4">
                <v-progress-circular indeterminate size="24" color="primary" />
              </div>

              <!-- No more data -->
              <div v-if="!hasMore && entities.length > 0" class="text-center pa-4 text-grey">
                Все сущности загружены
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Filters panel -->
      <v-slide-x-reverse-transition>
        <v-col v-if="panelVisible" cols="2" key="filters">
          <v-card>
            <v-card-title class="text-subtitle-1">Фильтры</v-card-title>
            <v-card-text class="pa-3">
              <!-- Favorites toggle -->
              <v-btn
                :color="filters.isFavorite ? 'amber' : 'default'"
                :variant="filters.isFavorite ? 'flat' : 'outlined'"
                block
                class="mb-4"
                @click="toggleFavoriteFilter"
              >
                <v-icon start :color="filters.isFavorite ? 'white' : 'amber'">mdi-star</v-icon>
                Избранные
              </v-btn>

              <!-- Search field -->
              <v-text-field
                v-model="filters.search"
                label="Поиск"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                class="mb-4"
                @update:model-value="onSearchChange"
              />

              <!-- Tracked only toggle -->
              <v-switch
                v-model="filters.trackedOnly"
                label="Только отслеживаемые"
                color="primary"
                density="compact"
                hide-details
                class="mb-4"
                @update:model-value="onFilterChange"
              />

              <!-- With notes toggle -->
              <v-switch
                v-model="filters.withNotes"
                label="С примечаниями"
                color="primary"
                density="compact"
                hide-details
                class="mb-4"
                @update:model-value="onFilterChange"
              />

              <!-- Show excluded toggle -->
              <v-switch
                v-model="filters.showExcluded"
                label="Показать исключённые"
                color="error"
                density="compact"
                hide-details
                class="mb-4"
                @update:model-value="onFilterChange"
              />

              <v-divider class="mb-4" />

              <!-- First seen date filters -->
              <div class="text-subtitle-2 mb-2">Первое упоминание</div>
              <v-text-field
                v-model="filters.firstSeenFrom"
                label="С"
                type="date"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-2"
                @update:model-value="onFilterChange"
              />
              <v-text-field
                v-model="filters.firstSeenTo"
                label="По"
                type="date"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
                @update:model-value="onFilterChange"
              />

              <!-- Last seen date filters -->
              <div class="text-subtitle-2 mb-2">Последнее упоминание</div>
              <v-text-field
                v-model="filters.lastSeenFrom"
                label="С"
                type="date"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-2"
                @update:model-value="onFilterChange"
              />
              <v-text-field
                v-model="filters.lastSeenTo"
                label="По"
                type="date"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
                @update:model-value="onFilterChange"
              />

              <!-- Reset filters -->
              <v-btn
                variant="text"
                color="primary"
                block
                prepend-icon="mdi-filter-remove"
                @click="resetFilters"
              >
                Сбросить фильтры
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-slide-x-reverse-transition>
    </v-row>

    <!-- Entity detail modal -->
    <v-dialog
      v-model="entityModalVisible"
      max-width="600px"
      @keydown.esc="closeEntityModal"
    >
      <v-card v-if="selectedEntity">
        <v-card-title class="d-flex justify-space-between align-center">
          <div class="d-flex align-center ga-2">
            <v-icon :color="getEntityIconColor(selectedEntity.entity_type)">
              {{ getEntityIcon(selectedEntity.entity_type) }}
            </v-icon>
            <span class="text-h6">{{ selectedEntity.display_name }}</span>
          </div>
          <v-btn icon="mdi-close" variant="text" @click="closeEntityModal" />
        </v-card-title>
        <v-divider />

        <v-card-text class="pa-4">
          <!-- Entity info -->
          <v-row>
            <v-col cols="12">
              <div class="text-subtitle-2 text-grey mb-1">Каноническое имя</div>
              <div class="text-body-1">{{ selectedEntity.canonical_name }}</div>
            </v-col>
          </v-row>

          <v-row v-if="selectedEntity.aliases && selectedEntity.aliases.length > 0">
            <v-col cols="12">
              <div class="text-subtitle-2 text-grey mb-1">Псевдонимы</div>
              <div class="d-flex flex-wrap ga-1">
                <v-chip
                  v-for="(alias, index) in selectedEntity.aliases"
                  :key="index"
                  size="small"
                  variant="outlined"
                >
                  {{ alias }}
                </v-chip>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <div class="text-subtitle-2 text-grey mb-1">Упоминаний</div>
              <v-chip
                color="primary"
                variant="tonal"
                class="cursor-pointer"
                @click="openMessagesForEntity(selectedEntity)"
                title="Показать сообщения с этой сущностью"
              >
                {{ selectedEntity.mention_count }}
              </v-chip>
            </v-col>
            <v-col cols="6">
              <div class="text-subtitle-2 text-grey mb-1">Тип</div>
              <v-select
                v-model="editForm.entity_type"
                :items="entityTypeOptions"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
          </v-row>

          <v-row v-if="selectedEntity.first_seen_at || selectedEntity.last_seen_at">
            <v-col cols="6" v-if="selectedEntity.first_seen_at">
              <div class="text-subtitle-2 text-grey mb-1">Первое упоминание</div>
              <div class="text-body-2">{{ formatDate(selectedEntity.first_seen_at) }}</div>
            </v-col>
            <v-col cols="6" v-if="selectedEntity.last_seen_at">
              <div class="text-subtitle-2 text-grey mb-1">Последнее упоминание</div>
              <div class="text-body-2">{{ formatDate(selectedEntity.last_seen_at) }}</div>
            </v-col>
          </v-row>

          <!-- Merge info -->
          <v-alert
            v-if="entityMergeInfo"
            type="info"
            variant="tonal"
            density="compact"
            class="mt-4"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-subtitle-2">Объединённая сущность</div>
                <div class="text-caption">
                  Объединено {{ entityMergeInfo.merged_entity_count + 1 }}
                  {{ pluralize(entityMergeInfo.merged_entity_count + 1, 'сущность', 'сущности', 'сущностей') }}:
                  {{ entityMergeInfo.merged_entity_names.join(', ') }}
                </div>
                <div class="text-caption text-grey">
                  {{ formatDate(entityMergeInfo.merged_at) }}
                </div>
              </div>
              <v-btn
                variant="outlined"
                size="small"
                color="warning"
                :loading="isReverting"
                @click="revertMerge"
              >
                Отменить объединение
              </v-btn>
            </div>
          </v-alert>

          <v-divider class="my-4" />

          <!-- Tracking toggle -->
          <v-row>
            <v-col cols="6">
              <v-switch
                v-model="editForm.is_tracked"
                label="Отслеживать"
                color="success"
                density="compact"
                hide-details
              />
            </v-col>
            <v-col cols="6">
              <v-switch
                v-model="editForm.is_excluded"
                label="Исключить"
                color="error"
                density="compact"
                hide-details
              />
            </v-col>
          </v-row>

          <!-- Display name (editable) -->
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="editForm.display_name"
                label="Отображаемое имя"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
          </v-row>

          <!-- User notes -->
          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="editForm.user_notes"
                label="Примечания"
                variant="outlined"
                density="compact"
                rows="3"
                hide-details
                placeholder="Добавьте примечание о сущности..."
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-btn
            color="error"
            variant="text"
            @click="confirmDelete"
          >
            Удалить
          </v-btn>
          <v-spacer />
          <v-btn
            color="default"
            variant="text"
            @click="closeEntityModal"
          >
            Отмена
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :loading="isSaving"
            @click="saveEntity"
          >
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation dialog -->
    <v-dialog
      v-model="deleteDialogVisible"
      max-width="400px"
    >
      <v-card>
        <v-card-title class="text-h6">Подтверждение удаления</v-card-title>
        <v-card-text>
          Вы уверены, что хотите удалить сущность "{{ selectedEntity?.display_name }}"?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="default"
            variant="text"
            @click="deleteDialogVisible = false"
          >
            Отмена
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            :loading="isDeleting"
            @click="deleteEntity"
          >
            Удалить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Merge modal -->
    <v-dialog
      v-model="mergeModalVisible"
      max-width="500px"
      @keydown.esc="closeMergeModal"
    >
      <v-card>
        <v-card-title class="d-flex align-center ga-2">
          <v-icon>mdi-merge</v-icon>
          Объединение сущностей
        </v-card-title>
        <v-divider />

        <v-card-text class="pa-4">
          <div class="text-body-2 mb-4">
            Выбрано сущностей для объединения: <strong>{{ selectedEntities.length }}</strong>
          </div>

          <div class="text-subtitle-2 mb-2">Выберите отображаемое имя:</div>
          <v-radio-group v-model="mergeDisplayName" class="mb-4">
            <v-radio
              v-for="entity in selectedEntityNames"
              :key="entity.id"
              :label="entity.name"
              :value="entity.name"
            />
          </v-radio-group>

          <v-text-field
            v-model="mergeDisplayName"
            label="Или введите своё название"
            variant="outlined"
            density="compact"
            hide-details
            class="mb-4"
          />

          <v-alert type="info" density="compact" variant="tonal" class="mb-0">
            Все псевдонимы будут объединены. Примечания будут склеены. Операцию можно отменить.
          </v-alert>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            variant="text"
            @click="closeMergeModal"
          >
            Отмена
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :loading="isMerging"
            :disabled="!mergeDisplayName"
            @click="executeMerge"
          >
            Объединить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for messages -->
    <v-snackbar
      v-model="snackbar.visible"
      :color="snackbar.color"
      timeout="3000"
    >
      {{ snackbar.text }}
    </v-snackbar>

  </v-container>
</template>

<script>
import NoProjectsAlert from "@/components/projects/NoProjectsAlert.vue";
import apiClient from "@/apiClient";

const COOKIE_PREFIX = 'entities_';
const COOKIE_MAX_AGE = 31536000; // 1 year

export default {
  name: "MonitoringEntities",
  components: { NoProjectsAlert },
  data() {
    return {
      noProjects: false,
      // Projects
      projects: [],
      selectedProjectId: null,
      // The project from the cookie is checked against the available ones before any request
      projectsChecked: false,

      // Active tab
      activeTab: 'PER',

      // Entities
      entities: [],
      totalEntities: 0,
      offset: 0,
      limit: 50,
      hasMore: true,
      isLoading: false,

      // Stats by type
      stats: {
        total_entities: 0,
        total_mentions: 0,
        by_type: []
      },

      // Sorting
      sortBy: 'mention_count',
      sortDesc: true,

      // Search and filters
      searchDebounceTimer: null,
      filters: {
        search: '',
        isFavorite: false,
        trackedOnly: false,
        withNotes: false,
        showExcluded: false,
        firstSeenFrom: null,
        firstSeenTo: null,
        lastSeenFrom: null,
        lastSeenTo: null
      },

      // UI state
      panelVisible: true,

      // Entity modal
      entityModalVisible: false,
      selectedEntity: null,
      editForm: {
        display_name: '',
        user_notes: '',
        is_tracked: false,
        is_excluded: false,
        entity_type: null
      },
      entityTypeOptions: [
        { title: 'Персона', value: 'PER' },
        { title: 'Организация', value: 'ORG' },
        { title: 'Локация', value: 'LOC' },
        { title: 'Геополит. объект', value: 'GPE' }
      ],
      isSaving: false,

      // Delete dialog
      deleteDialogVisible: false,
      isDeleting: false,

      // Snackbar
      snackbar: {
        visible: false,
        text: '',
        color: 'success'
      },

      // Selection and bulk actions
      selectedEntities: [],
      selectedBulkAction: null,
      bulkActionLoading: false,
      bulkActions: [
        { title: 'Добавить в избранное', value: 'add_favorite' },
        { title: 'Удалить из избранного', value: 'remove_favorite' },
        { title: 'Исключить', value: 'exclude' },
        { title: 'Вернуть из исключённых', value: 'include' },
        { title: 'Объединить', value: 'merge' }
      ],

      // Merge modal
      mergeModalVisible: false,
      mergeDisplayName: '',
      isMerging: false,

      // Merge info for selected entity
      entityMergeInfo: null,
      isReverting: false
    };
  },
  watch: {
    activeTab() {
      this.saveFiltersToCoookies();
      this.resetAndLoad();
    },
    panelVisible(newValue) {
      this.setCookie('panel', newValue ? '1' : '0');
    }
  },
  computed: {
    typeCounts() {
      const counts = { PER: 0, ORG: 0, LOC: 0, GPE: 0 };
      if (this.stats && this.stats.by_type) {
        for (const item of this.stats.by_type) {
          if (item.entity_type && Object.prototype.hasOwnProperty.call(counts, item.entity_type)) {
            counts[item.entity_type] = item.total_count || 0;
          }
        }
      }
      return counts;
    },
    hasActiveFilters() {
      return this.filters.search ||
             this.filters.isFavorite ||
             this.filters.trackedOnly ||
             this.filters.withNotes ||
             this.filters.showExcluded ||
             this.filters.firstSeenFrom ||
             this.filters.firstSeenTo ||
             this.filters.lastSeenFrom ||
             this.filters.lastSeenTo;
    },
    selectedEntityNames() {
      return this.selectedEntities.map(id => {
        const entity = this.entities.find(e => e.id === id);
        return entity ? { id, name: entity.display_name, canonical: entity.canonical_name } : null;
      }).filter(e => e !== null);
    },
    allSelected() {
      return this.entities.length > 0 && this.selectedEntities.length === this.entities.length;
    },
    someSelected() {
      return this.selectedEntities.length > 0 && this.selectedEntities.length < this.entities.length;
    },
    bulkActionsForSelect() {
      return this.bulkActions.map(action => ({
        ...action,
        props: {
          disabled: action.value === 'merge' && this.selectedEntities.length < 2
        }
      }));
    }
  },
  methods: {
    // Cookie helpers
    setCookie(key, value) {
      document.cookie = `${COOKIE_PREFIX}${key}=${encodeURIComponent(value)};path=/;max-age=${COOKIE_MAX_AGE}`;
    },
    getCookie(key) {
      const match = document.cookie.match(new RegExp(`${COOKIE_PREFIX}${key}=([^;]+)`));
      return match ? decodeURIComponent(match[1]) : null;
    },
    loadFiltersFromCookies() {
      // Panel visibility
      const panel = this.getCookie('panel');
      if (panel !== null) {
        this.panelVisible = panel === '1';
      }

      // Project
      const projectId = this.getCookie('project');
      if (projectId) {
        this.selectedProjectId = parseInt(projectId, 10);
      }

      // Active tab
      const tab = this.getCookie('tab');
      if (tab && ['PER', 'ORG', 'LOC', 'GPE'].includes(tab)) {
        this.activeTab = tab;
      }

      // Sorting
      const sortBy = this.getCookie('sortBy');
      if (sortBy) {
        this.sortBy = sortBy;
      }
      const sortDesc = this.getCookie('sortDesc');
      if (sortDesc !== null) {
        this.sortDesc = sortDesc === '1';
      }

      // Filters
      const search = this.getCookie('search');
      if (search) this.filters.search = search;

      const isFavorite = this.getCookie('isFavorite');
      if (isFavorite !== null) this.filters.isFavorite = isFavorite === '1';

      const trackedOnly = this.getCookie('trackedOnly');
      if (trackedOnly !== null) this.filters.trackedOnly = trackedOnly === '1';

      const withNotes = this.getCookie('withNotes');
      if (withNotes !== null) this.filters.withNotes = withNotes === '1';

      const showExcluded = this.getCookie('showExcluded');
      if (showExcluded !== null) this.filters.showExcluded = showExcluded === '1';

      const firstSeenFrom = this.getCookie('firstSeenFrom');
      if (firstSeenFrom) this.filters.firstSeenFrom = firstSeenFrom;

      const firstSeenTo = this.getCookie('firstSeenTo');
      if (firstSeenTo) this.filters.firstSeenTo = firstSeenTo;

      const lastSeenFrom = this.getCookie('lastSeenFrom');
      if (lastSeenFrom) this.filters.lastSeenFrom = lastSeenFrom;

      const lastSeenTo = this.getCookie('lastSeenTo');
      if (lastSeenTo) this.filters.lastSeenTo = lastSeenTo;
    },
    saveFiltersToCoookies() {
      this.setCookie('tab', this.activeTab);
      this.setCookie('sortBy', this.sortBy);
      this.setCookie('sortDesc', this.sortDesc ? '1' : '0');
      this.setCookie('search', this.filters.search || '');
      this.setCookie('isFavorite', this.filters.isFavorite ? '1' : '0');
      this.setCookie('trackedOnly', this.filters.trackedOnly ? '1' : '0');
      this.setCookie('withNotes', this.filters.withNotes ? '1' : '0');
      this.setCookie('firstSeenFrom', this.filters.firstSeenFrom || '');
      this.setCookie('firstSeenTo', this.filters.firstSeenTo || '');
      this.setCookie('lastSeenFrom', this.filters.lastSeenFrom || '');
      this.setCookie('lastSeenTo', this.filters.lastSeenTo || '');
      this.setCookie('showExcluded', this.filters.showExcluded ? '1' : '0');
    },

    async loadProjects() {
      try {
        const response = await apiClient.get('/available-projects');
        this.projects = response.data;
        this.noProjects = !this.projects.length;

        if (this.projects.length > 0 && !this.selectedProjectId) {
          this.selectedProjectId = this.projects[0].id;
        }
        // Validate saved project exists
        if (this.selectedProjectId && !this.projects.some(p => p.id === this.selectedProjectId)) {
          this.selectedProjectId = this.projects[0]?.id || null;
        }
        this.projectsChecked = true;
      } catch (error) {
        console.error('Error loading projects:', error);
        this.showMessage('Ошибка загрузки проектов', 'error');
      }
    },

    selectProject(projectId) {
      if (this.selectedProjectId === projectId) return;
      this.selectedProjectId = projectId;
      this.setCookie('project', projectId);
      this.resetAndLoad();
      this.loadStats();
    },

    toggleSort(field) {
      if (this.sortBy === field) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = field;
        this.sortDesc = true;
      }
      this.saveFiltersToCoookies();
      this.resetAndLoad();
    },

    async loadEntities(append = false) {
      if (!this.projectsChecked || !this.selectedProjectId) return;
      if (this.isLoading) return;

      this.isLoading = true;

      try {
        const params = {
          entity_type: this.activeTab,
          offset: append ? this.offset : 0,
          limit: this.limit,
          sort_by: this.sortBy,
          sort_desc: this.sortDesc
        };

        if (this.filters.search && this.filters.search.trim()) {
          params.search = this.filters.search.trim();
        }

        if (this.filters.isFavorite) {
          params.is_favorite = true;
        }

        if (this.filters.trackedOnly) {
          params.is_tracked = true;
        }

        if (this.filters.withNotes) {
          params.has_notes = true;
        }

        // By default hide excluded, show only when filter enabled
        if (this.filters.showExcluded) {
          params.is_excluded = true;
        } else {
          params.is_excluded = false;
        }

        if (this.filters.firstSeenFrom) {
          params.first_seen_from = this.filters.firstSeenFrom + 'T00:00:00';
        }
        if (this.filters.firstSeenTo) {
          params.first_seen_to = this.filters.firstSeenTo + 'T23:59:59';
        }
        if (this.filters.lastSeenFrom) {
          params.last_seen_from = this.filters.lastSeenFrom + 'T00:00:00';
        }
        if (this.filters.lastSeenTo) {
          params.last_seen_to = this.filters.lastSeenTo + 'T23:59:59';
        }

        const response = await apiClient.get(
          `/projects/${this.selectedProjectId}/entities`,
          { params }
        );

        const newEntities = response.data.items || [];

        if (append) {
          this.entities = [...this.entities, ...newEntities];
        } else {
          this.entities = newEntities;
        }

        this.totalEntities = response.data.total || 0;
        this.offset = this.entities.length;
        this.hasMore = this.entities.length < this.totalEntities;

      } catch (error) {
        console.error('Error loading entities:', error);
        this.showMessage('Ошибка загрузки сущностей', 'error');
      } finally {
        this.isLoading = false;
      }
    },

    async loadStats() {
      if (!this.projectsChecked || !this.selectedProjectId) return;

      try {
        const response = await apiClient.get(
          `/projects/${this.selectedProjectId}/entities/stats`
        );
        this.stats = response.data || { total_entities: 0, total_mentions: 0, by_type: [] };
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    },

    resetAndLoad() {
      this.entities = [];
      this.offset = 0;
      this.hasMore = true;
      this.selectedEntities = [];
      this.loadEntities();
    },

    onSearchChange() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.saveFiltersToCoookies();
        this.resetAndLoad();
      }, 300);
    },

    onFilterChange() {
      this.saveFiltersToCoookies();
      this.resetAndLoad();
    },

    resetFilters() {
      this.filters = {
        search: '',
        isFavorite: false,
        trackedOnly: false,
        withNotes: false,
        showExcluded: false,
        firstSeenFrom: null,
        firstSeenTo: null,
        lastSeenFrom: null,
        lastSeenTo: null
      };
      this.saveFiltersToCoookies();
      this.resetAndLoad();
    },

    handleScroll(event) {
      const container = event.target;
      const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;

      if (scrollBottom < 200 && this.hasMore && !this.isLoading) {
        this.loadEntities(true);
      }
    },

    async openEntityModal(entity) {
      this.selectedEntity = entity;
      this.editForm = {
        display_name: entity.display_name,
        user_notes: entity.user_notes || '',
        is_tracked: entity.is_tracked || false,
        is_excluded: entity.is_excluded || false,
        entity_type: entity.entity_type
      };
      this.entityMergeInfo = null;
      this.entityModalVisible = true;

      // Check if entity has merge history
      try {
        const response = await apiClient.get(
          `/projects/${this.selectedProjectId}/entities/merge-history`,
          { params: { entity_id: entity.id } }
        );
        if (response.data.items && response.data.items.length > 0) {
          this.entityMergeInfo = response.data.items[0];
        }
      } catch (error) {
        console.error('Error loading merge history:', error);
      }
    },

    openMessagesForEntity(entity) {
      // Open Messages page in new tab with entity filter
      // Pass entity_id - Data Service will fetch finding_ids from Core
      const route = this.$router.resolve({
        name: 'monitoringMessages',
        query: {
          entity_id: entity.id,
          entity_name: entity.display_name,
          project_id: this.selectedProjectId
        }
      });
      window.open(route.href, '_blank');
    },

    async toggleFavorite(entity) {
      try {
        const response = await apiClient.post(
          `/projects/${this.selectedProjectId}/entities/${entity.id}/toggle-favorite`
        );
        // Update local state
        entity.is_favorite = response.data.is_favorite;
      } catch (error) {
        console.error('Error toggling favorite:', error);
        this.$emit('show-message', 'Ошибка при изменении избранного', true);
      }
    },

    toggleFavoriteFilter() {
      this.filters.isFavorite = !this.filters.isFavorite;
      this.saveFiltersToCoookies();
      this.resetAndLoad();
    },

    closeEntityModal() {
      this.entityModalVisible = false;
      this.selectedEntity = null;
      this.entityMergeInfo = null;
      this.editForm = {
        display_name: '',
        user_notes: '',
        is_tracked: false,
        is_excluded: false,
        entity_type: null
      };
    },

    async saveEntity() {
      if (!this.selectedEntity) return;

      this.isSaving = true;

      try {
        // Change type if needed (separate endpoint)
        if (this.editForm.entity_type !== this.selectedEntity.entity_type) {
          await apiClient.post(
            `/projects/${this.selectedProjectId}/entities/${this.selectedEntity.id}/change-type`,
            { new_type: this.editForm.entity_type }
          );
        }

        await apiClient.patch(
          `/projects/${this.selectedProjectId}/entities/${this.selectedEntity.id}`,
          {
            display_name: this.editForm.display_name,
            user_notes: this.editForm.user_notes,
            is_tracked: this.editForm.is_tracked,
            is_excluded: this.editForm.is_excluded
          }
        );

        this.showMessage('Сущность сохранена', 'success');
        this.closeEntityModal();
        this.resetAndLoad();
        this.loadStats();
      } catch (error) {
        console.error('Error saving entity:', error);
        const detail = error.response?.data?.detail || 'Ошибка сохранения';
        this.showMessage(detail, 'error');
      } finally {
        this.isSaving = false;
      }
    },

    confirmDelete() {
      this.deleteDialogVisible = true;
    },

    async deleteEntity() {
      if (!this.selectedEntity) return;

      this.isDeleting = true;

      try {
        await apiClient.delete(
          `/projects/${this.selectedProjectId}/entities/${this.selectedEntity.id}`
        );

        this.showMessage('Сущность удалена', 'success');
        this.deleteDialogVisible = false;
        this.closeEntityModal();
        this.resetAndLoad();
        this.loadStats();
      } catch (error) {
        console.error('Error deleting entity:', error);
        this.showMessage('Ошибка удаления', 'error');
      } finally {
        this.isDeleting = false;
      }
    },

    getEntityIcon(type) {
      const icons = {
        'PER': 'mdi-account',
        'ORG': 'mdi-domain',
        'LOC': 'mdi-map-marker',
        'GPE': 'mdi-earth'
      };
      return icons[type] || 'mdi-tag';
    },

    getEntityIconColor(type) {
      const colors = {
        'PER': 'blue',
        'ORG': 'purple',
        'LOC': 'green',
        'GPE': 'orange'
      };
      return colors[type] || 'grey';
    },

    getEntityTypeName(type) {
      const names = {
        'PER': 'Персона',
        'ORG': 'Организация',
        'LOC': 'Локация',
        'GPE': 'Страна/Союз'
      };
      return names[type] || type;
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    formatDateShort(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    showMessage(text, color = 'success') {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.visible = true;
    },

    pluralize(count, one, few, many) {
      // Russian pluralization: 1 сущность, 2-4 сущности, 5-20 сущностей
      const n = Math.abs(count) % 100;
      const n1 = n % 10;
      if (n > 10 && n < 20) return many;
      if (n1 > 1 && n1 < 5) return few;
      if (n1 === 1) return one;
      return many;
    },

    // Selection methods
    toggleSelectAll(value) {
      if (value) {
        this.selectedEntities = this.entities.map(e => e.id);
      } else {
        this.selectedEntities = [];
      }
    },

    toggleEntitySelection(entityId) {
      const index = this.selectedEntities.indexOf(entityId);
      if (index === -1) {
        this.selectedEntities.push(entityId);
      } else {
        this.selectedEntities.splice(index, 1);
      }
    },

    clearSelection() {
      this.selectedEntities = [];
      this.selectedBulkAction = null;
    },

    // Bulk actions
    async executeBulkAction(action) {
      if (!action || this.selectedEntities.length === 0) return;

      if (action === 'merge') {
        if (this.selectedEntities.length < 2) {
          this.showMessage('Для объединения нужно выбрать минимум 2 сущности', 'warning');
          this.selectedBulkAction = null;
          return;
        }
        // Set default display name from first selected
        this.mergeDisplayName = this.selectedEntityNames[0]?.name || '';
        this.mergeModalVisible = true;
        return;
      }

      // Execute add_favorite or remove_favorite
      this.bulkActionLoading = true;
      try {
        const response = await apiClient.post(
          `/projects/${this.selectedProjectId}/entities/bulk-action`,
          {
            action: action,
            entity_ids: this.selectedEntities
          }
        );

        this.showMessage(response.data.message || 'Действие выполнено');
        this.clearSelection();
        this.resetAndLoad();
      } catch (error) {
        console.error('Bulk action error:', error);
        this.showMessage('Ошибка при выполнении действия', 'error');
      } finally {
        this.bulkActionLoading = false;
        this.selectedBulkAction = null;
      }
    },

    // Merge modal
    closeMergeModal() {
      this.mergeModalVisible = false;
      this.mergeDisplayName = '';
      this.selectedBulkAction = null;
    },

    async executeMerge() {
      if (!this.mergeDisplayName || this.selectedEntities.length < 2) return;

      this.isMerging = true;
      try {
        const response = await apiClient.post(
          `/projects/${this.selectedProjectId}/entities/bulk-action`,
          {
            action: 'merge',
            entity_ids: this.selectedEntities,
            merge_options: {
              display_name: this.mergeDisplayName
            }
          }
        );

        this.showMessage(response.data.message || 'Сущности объединены');
        this.closeMergeModal();
        this.clearSelection();
        this.resetAndLoad();
        this.loadStats();
      } catch (error) {
        console.error('Merge error:', error);
        const message = error.response?.data?.detail || 'Ошибка при объединении';
        this.showMessage(message, 'error');
      } finally {
        this.isMerging = false;
      }
    },

    async revertMerge() {
      if (!this.entityMergeInfo) return;

      this.isReverting = true;
      try {
        await apiClient.post(
          `/projects/${this.selectedProjectId}/entities/merges/${this.entityMergeInfo.merge_id}/revert`
        );

        this.showMessage('Объединение отменено');
        this.closeEntityModal();
        this.resetAndLoad();
        this.loadStats();
      } catch (error) {
        console.error('Revert error:', error);
        const message = error.response?.data?.detail || 'Ошибка при отмене';
        this.showMessage(message, 'error');
      } finally {
        this.isReverting = false;
      }
    }
  },

  async mounted() {
    this.loadFiltersFromCookies();
    await this.loadProjects();
    if (this.selectedProjectId) {
      await Promise.all([
        this.loadEntities(),
        this.loadStats()
      ]);
    }
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.table-column-transition {
  transition: flex-basis 0.3s ease, max-width 0.3s ease;
}

.entities-container {
  max-height: calc(100vh - 336px);
  overflow-y: auto;
}

.entity-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.sortable-header:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.favorite-star {
  cursor: pointer;
  transition: all 0.2s ease;
}

.favorite-star:hover {
  transform: scale(1.2);
}

.favorite-active {
  filter: drop-shadow(0 0 4px #ffc107);
}

.bulk-actions-bar {
  background-color: rgba(var(--v-theme-primary), 0.08);
  border-bottom: 1px solid rgba(var(--v-theme-primary), 0.2);
}

.entity-selected {
  background-color: rgba(var(--v-theme-primary), 0.08) !important;
}
</style>
